/**
 * Workshop Filings Receipt Slip Route — Unified Print Engine (AG-004).
 */
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useJobCards } from "@/lib/jobcards-store";
import { PrintEngine } from "@/components/print-engine/PrintEngine";
import { useSettings } from "@/lib/settings-store";
import { shortShopName } from "@/lib/app-info";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/workshop/filings-slip/$id")({
  head: () => {
    const shortName = shortShopName(useSettings.getState().firm?.shopName);
    return {
      meta: [{ title: `Filings Receipt · ${shortName} ERP` }],
    };
  },
  component: FilingsReceiptPage,
});

function FilingsReceiptPage() {
  const { id } = useParams({ from: "/workshop/filings-slip/$id" });
  const job = useJobCards((s) =>
    s.jobs.find((j) => j.id === id || j.id.toLowerCase() === id.toLowerCase()),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const checkOrFetch = async () => {
      if (job?.workReceipt) {
        if (active) setLoading(false);
        return;
      }
      try {
        await useJobCards.getState().refresh?.();
      } catch (e) {
        console.error("Failed to refresh job cards:", e);
      } finally {
        if (active) setLoading(false);
      }
    };
    void checkOrFetch();
    return () => {
      active = false;
    };
  }, [id, job?.workReceipt]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-background text-foreground">
        <div className="text-center space-y-3 flex flex-col items-center">
          <Loader2 className="h-8 w-8 text-gold animate-spin" />
          <p className="text-sm text-muted-foreground">Loading Filings Receipt…</p>
        </div>
      </div>
    );
  }

  if (!job || !job.workReceipt || job.workReceipt.filingsFineMg <= 0) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground p-6">
        <div className="text-center space-y-3">
          <h1 className="font-serif text-2xl text-gold">No filings recorded</h1>
          <p className="text-sm text-muted-foreground">
            No filings return has been recorded for Job Card #{id?.slice(-6)?.toUpperCase()}.
          </p>
          <Link to="/workshop" className="text-gold underline">
            Back to Workshop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PrintEngine
      docType="filings_receipt"
      recordId={job.id}
      backUrl={`/workshop/${job.id}`}
    />
  );
}
